import { useEffect, useMemo, useState } from "react";
import { UserPlus, Trash2, Chrome, FileUp, ArrowLeft, Pencil, CalendarRange } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", employee_id: "", job_number: "", specialty: "", rank_level: "", current_job: "معلم", phone: "" };

const JOB_OPTIONS = ["مدير", "وكيل", "موجه طلابي", "موجه صحي", "رائد نشاط", "معلم", "محضر مختبر", "إداري", "حارس", "غير ذلك"];

// قيم قديمة خارج القائمة تُعرض كخيار إضافي حتى لا تُفقد بيانات الموظفين الحاليين
const JobSelect = ({ value, onChange, testId, compact = false }) => {
  const options = value && !JOB_OPTIONS.includes(value) ? [value, ...JOB_OPTIONS] : JOB_OPTIONS;
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={compact
        ? "!h-7 !w-auto !min-w-[92px] !border-0 !shadow-none !bg-transparent hover:!bg-emerald-50 !rounded-md !px-1.5 !py-0 !mx-auto text-xs font-semibold text-emerald-900 underline decoration-dotted underline-offset-4 cursor-pointer"
        : "w-full h-10 text-sm bg-white"}>
        <span className={`flex-1 truncate text-right ${value ? "" : "text-slate-400 font-normal"}`}
          data-testid={`${testId}-value`}>{value || "اختر العمل الحالي"}</span>
      </SelectTrigger>
      <SelectContent>
        {options.map((j, i) => (
          <SelectItem key={j} value={j} data-testid={`${testId}-opt-${i}`}>{j}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const IMPORT_FIELDS = [
  { key: "name", label: "الاسم الكامل", required: true },
  { key: "employee_id", label: "رقم الهوية (السجل المدني)" },
  { key: "specialty", label: "التخصص" },
  { key: "rank_level", label: "المستوى / المرتبة" },
  { key: "job_number", label: "رقم الوظيفة" },
  { key: "current_job", label: "العمل الحالي" },
  { key: "phone", label: "رقم الجوال" },
];

const GUESS = {
  name: ["الاسم", "اسم الموظف", "اسم المعلم", "الموظف", "المعلم"],
  employee_id: ["رقم الهوية", "الهوية", "السجل المدني", "هوية", "السجل"],
  specialty: ["التخصص", "تخصص"],
  rank_level: ["المستوى", "المرتبة", "مرتبة"],
  job_number: ["رقم الوظيفة", "الرقم الوظيفي", "رقم الوظيفه", "الوظيفة"],
  current_job: ["العمل الحالي", "طبيعة العمل", "العمل"],
  phone: ["الجوال", "الهاتف", "جوال", "جواله"],
};

const arNormalize = (s) => String(s ?? "")
  .replace(/[ً-ْٰـ]/g, "")
  .replace(/[أإآ]/g, "ا")
  .replace(/ى/g, "ي")
  .replace(/ة/g, "ه")
  .replace(/[ؤئ]/g, "ء");

const guessMapping = (headers) => {
  const taken = new Set();
  const map = {};
  for (const f of IMPORT_FIELDS) {
    for (const kw of GUESS[f.key]) {
      const idx = headers.findIndex((h, i) => !taken.has(i) && arNormalize(h).includes(arNormalize(kw)));
      if (idx >= 0) { map[f.key] = String(idx); taken.add(idx); break; }
    }
  }
  return map;
};

export default function EmployeesTab() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [exclOpen, setExclOpen] = useState(false);
  const [exclFrom, setExclFrom] = useState("");
  const [exclTo, setExclTo] = useState("");
  const [exclSel, setExclSel] = useState(() => new Set());
  const [exclSaving, setExclSaving] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [exclEdit, setExclEdit] = useState(null);

  const savePeriodExclusion = async () => {
    if (!exclFrom || !exclTo || exclFrom > exclTo) { toast.error("تأكد من تاريخي البداية والنهاية"); return; }
    if (exclSel.size === 0) { toast.error("حدد موظفاً واحداً على الأقل"); return; }
    setExclSaving(true);
    try {
      if (exclEdit) {
        const res = await api.put("/employees/period-exclusions", {
          old_from: exclEdit.from, old_to: exclEdit.to,
          from_date: exclFrom, to_date: exclTo, employee_ids: [...exclSel],
        });
        toast.success(`تم تحديث الفترة ${res.data.from} ← ${res.data.to} (${res.data.kept + res.data.added} موظف)`);
      } else {
        const res = await api.post("/employees/period-exclusion", {
          from_date: exclFrom, to_date: exclTo, employee_ids: [...exclSel],
        });
        toast.success(`تم تطبيق الاستثناء على ${res.data.updated} موظف من ${res.data.from} إلى ${res.data.to}` +
          (res.data.purged_records ? ` — وحُذفت ${res.data.purged_records} سجل دوام ضمن الفترة` : ""));
      }
      setExclOpen(false);
      setExclSel(new Set());
      setExclEdit(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل حفظ الاستثناء");
    } finally { setExclSaving(false); }
  };

  const periodGroups = useMemo(() => {
    const m = new Map();
    for (const p of periods) {
      const key = `${p.from}|${p.to}`;
      if (!m.has(key)) m.set(key, { from: p.from, to: p.to, employees: [] });
      m.get(key).employees.push(p);
    }
    return [...m.values()];
  }, [periods]);

  const openCreatePeriod = () => {
    setExclEdit(null); setExclFrom(""); setExclTo(""); setExclSel(new Set()); setExclOpen(true);
  };

  const openEditPeriod = (g) => {
    setExclEdit({ from: g.from, to: g.to });
    setExclFrom(g.from); setExclTo(g.to);
    setExclSel(new Set(g.employees.map((e) => e.emp_id)));
    setExclOpen(true);
  };

  const deletePeriodGroup = async (g) => {
    if (!window.confirm(`حذف فترة الاستثناء (${g.from} ← ${g.to}) عن جميع الموظفين (${g.employees.length})؟`)) return;
    try {
      await api.delete("/employees/period-exclusions", { data: { from_date: g.from, to_date: g.to } });
      toast.success("تم حذف فترة الاستثناء — ستُحتسب مساءلات تلك الفترة من جديد");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل حذف الفترة");
    }
  };

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/employees").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/employees/period-exclusions").then((r) => setPeriods(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/employees", form);
      toast.success("تم حفظ الموظف");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "تعذر حفظ الموظف");
    }
  };

  const saveJob = async (emp, job) => {
    const prev = emp.current_job || "";
    if ((prev || "معلم") === job) return;
    setRows((rs) => rs.map((r) => (r.id === emp.id ? { ...r, current_job: job } : r)));
    try {
      await api.put(`/employees/${emp.id}`, {
        name: emp.name, employee_id: emp.employee_id, specialty: emp.specialty,
        rank_level: emp.rank_level, job_number: emp.job_number, phone: emp.phone,
        current_job: job,
      });
      toast.success(`تم تحديث العمل الحالي: ${job}`);
    } catch {
      setRows((rs) => rs.map((r) => (r.id === emp.id ? { ...r, current_job: prev } : r)));
      toast.error("تعذر تحديث العمل الحالي");
    }
  };

  const toggleExclude = async (emp) => {
    const next = !emp.excluded;
    setRows((rs) => rs.map((r) => (r.id === emp.id ? { ...r, excluded: next } : r)));
    try {
      await api.patch(`/employees/${emp.id}/exclusion`, { excluded: next });
      toast.success(next
        ? `تم استثناء "${emp.name}" — لن يظهر في سجل الدوام والتقارير والمساءلات وتقييم الأداء`
        : `تم إلغاء استثناء "${emp.name}"`);
    } catch {
      setRows((rs) => rs.map((r) => (r.id === emp.id ? { ...r, excluded: emp.excluded } : r)));
      toast.error("تعذر تحديث الاستثناء");
    }
  };

  const [editEmp, setEditEmp] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const openEdit = (emp) => {
    setEditEmp(emp);
    setEditForm({
      name: emp.name || "", employee_id: emp.employee_id || "", job_number: emp.job_number || "",
      specialty: emp.specialty || "", rank_level: emp.rank_level || "",
      current_job: emp.current_job || "معلم", phone: emp.phone || "",
      weekly_exemption: emp.weekly_exemption || { days: [], types: [] },
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { toast.error("اسم الموظف مطلوب"); return; }
    setBusy(true);
    try {
      const res = await api.put(`/employees/${editEmp.id}`, editForm);
      const n = (res.data?.records_updated || 0) + (res.data?.inquiries_updated || 0);
      toast.success(n > 0
        ? `تم تحديث بيانات الموظف وربط الاسم الجديد في ${n} سجل/مساءلة`
        : "تم تحديث بيانات الموظف");
      setEditEmp(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التحديث");
    } finally { setBusy(false); }
  };

  const remove = async (emp) => {
    if (!window.confirm(`حذف الموظف "${emp.name}" من الدليل؟ (سجلات الحضور تبقى محفوظة)`)) return;
    await api.delete(`/employees/${emp.id}`).then(() => { toast.success("تم الحذف"); load(); })
      .catch(() => toast.error("فشل الحذف"));
  };

  const removeAll = async () => {
    if (!window.confirm("تنبيه: سيتم حذف جميع الموظفين نهائياً من الدليل. هل أنت متأكد؟")) return;
    if (!window.confirm("تأكيد أخير: لا يمكن التراجع عن هذا الإجراء. متابعة حذف الجميع؟")) return;
    await api.delete("/employees")
      .then((r) => { toast.success(`تم حذف ${r.data.deleted} موظف`); load(); })
      .catch(() => toast.error("فشل الحذف"));
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/employees/excel-preview", fd);
      if (!data.headers?.length) { toast.error("الملف فارغ"); return; }
      setImportFile(f);
      setPreview(data);
      setMapping(guessMapping(data.headers));
    } catch (err) {
      toast.error(err.response?.data?.detail || "تعذر قراءة الملف");
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    if (!mapping.name) { toast.error("حدد عمود الاسم أولاً"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("mapping", JSON.stringify(mapping));
      const { data } = await api.post("/employees/excel-import", fd);
      toast.success(`تم استيراد ${data.imported} موظف${data.duplicates ? ` (تم تخطي ${data.duplicates} مكرر بنفس رقم الهوية)` : ""}${data.skipped ? ` (تم تخطي ${data.skipped} صف بدون اسم)` : ""}`);
      setImportOpen(false);
      setPreview(null);
      setImportFile(null);
      setMapping({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الاستيراد");
    } finally { setBusy(false); }
  };

  const field = (key, label, placeholder, dir, testId) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={form[key]} data-testid={testId} dir={dir}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder} />
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border shadow-sm" data-testid="employees-tab">
      <div className="p-4 border-b flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border rounded-lg px-3 py-2">
          <Chrome className="w-4 h-4 text-emerald-800" />
          تُستورد أسماء الموظفين تلقائياً من حضوري عبر إضافة كروم، وبيانات التخصص والمرتبة تُستخدم في نماذج المسائلات الرسمية
        </div>
        <div className="mr-auto flex gap-2">
          {rows && rows.length > 0 && (
            <Button size="sm" variant="outline" onClick={removeAll}
              data-testid="btn-delete-all-employees"
              className="h-9 text-xs gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50">
              <Trash2 className="w-4 h-4" /> حذف جميع الموظفين
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={openCreatePeriod}
            data-testid="btn-period-exclusion" className="h-9 text-xs gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50">
            <CalendarRange className="w-4 h-4" /> استثناء من فترة
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}
            data-testid="btn-import-employees-excel" className="h-9 text-xs gap-1.5">
            <FileUp className="w-4 h-4 text-blue-700" /> استيراد من Excel
          </Button>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="btn-add-employee"
            className="h-9 text-xs gap-1.5 bg-emerald-900 hover:bg-emerald-950 text-white">
            <UserPlus className="w-4 h-4" /> إضافة موظف يدوياً
          </Button>
        </div>
      </div>
      {periodGroups.length > 0 && (
        <div className="p-4 border-b bg-amber-50/40" data-testid="period-exclusions-section">
          <div className="flex items-center gap-2 mb-2">
            <CalendarRange className="w-4 h-4 text-amber-700" />
            <h3 className="text-sm font-bold text-amber-900">الفترات المستثناة ({periodGroups.length})</h3>
            <span className="text-[11px] text-slate-400">— اضغط على الفترة لتعديل تاريخها أو موظفيها</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {periodGroups.map((g, i) => (
              <div key={`${g.from}-${g.to}-${i}`} data-testid={`period-exclusion-row-${i}`}
                className="flex items-center gap-1 bg-white border border-amber-200 rounded-xl ps-1 pe-1.5 py-1 text-xs shadow-sm">
                <button onClick={() => openEditPeriod(g)} data-testid={`btn-edit-period-${i}`}
                  title="تعديل الفترة وموظفيها"
                  className="flex items-center gap-2 hover:bg-amber-50 rounded-lg px-2 py-0.5 transition-colors">
                  <span className="font-mono text-amber-800 bg-amber-100/70 rounded-md px-1.5 py-0.5" dir="ltr"
                    data-testid={`period-exclusion-span-${i}`}>{g.from} ← {g.to}</span>
                  <span className="text-[10px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5"
                    data-testid={`period-exclusion-count-${i}`}>{g.employees.length} موظف</span>
                </button>
                <button onClick={() => deletePeriodGroup(g)} data-testid={`btn-delete-period-${i}`}
                  title="حذف الفترة عن جميع الموظفين" className="text-rose-600 hover:text-rose-800 p-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="table-employees">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs">
              {["م", "اسم الموظف", "رقم الهوية", "التخصص", "المستوى / المرتبة", "العمل الحالي", "رقم الجوال", "استثناء", "تعديل", "إجراء"].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold text-center whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={10} className="text-center py-10 text-slate-400">جارٍ التحميل...</td></tr>}
            {rows !== null && rows.length === 0 &&
              <tr><td colSpan={10} className="text-center py-10 text-slate-400">لا يوجد موظفون — استورد من حضوري أو ملف Excel أو أضف يدوياً</td></tr>}
            {rows && rows.map((e, i) => (
              <tr key={e.id} className={`border-t hover:bg-slate-50/60 ${e.excluded ? "opacity-50" : ""}`}
                data-testid={`employee-row-${e.id}`} data-excluded={e.excluded ? "true" : "false"}>
                <td className="px-3 py-2.5 text-xs text-slate-400 font-mono text-center w-10" data-testid={`serial-${e.id}`}>{i + 1}</td>
                <td className="px-4 py-2.5 font-semibold whitespace-nowrap text-center">
                  {e.name}
                  {e.excluded && (
                    <span className="mr-2 text-[10px] font-bold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5"
                      data-testid={`badge-excluded-${e.id}`}>مستثنى</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-slate-500 font-mono text-xs text-center" dir="ltr">{e.employee_id || "—"}</td>
                <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap text-center">{e.specialty || "—"}</td>
                <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap text-center">{e.rank_level || "—"}</td>
                <td className="px-2 py-1.5 whitespace-nowrap text-center">
                  <JobSelect compact value={e.current_job || "معلم"} testId={`job-inline-select-${e.id}`}
                    onChange={(v) => saveJob(e, v)} />
                </td>
                <td className="px-4 py-2.5 text-slate-500 font-mono text-xs text-center" dir="ltr">{e.phone || "—"}</td>
                <td className="px-4 py-2.5 text-center">
                  <Switch checked={!!e.excluded} onCheckedChange={() => toggleExclude(e)}
                    data-testid={`switch-exclude-${e.id}`} aria-label={`استثناء ${e.name}`} />
                </td>
                <td className="px-4 py-2.5 text-center">
                  <button onClick={() => openEdit(e)} data-testid={`btn-edit-employee-${e.id}`}
                    title="تعديل بيانات الموظف"
                    className="text-emerald-700 hover:text-emerald-900 p-1.5">
                    <Pencil className="w-4 h-4" />
                  </button>
                </td>
                <td className="px-4 py-2.5 text-center">
                  <button onClick={() => remove(e)} data-testid={`btn-delete-employee-${e.id}`}
                    className="text-rose-600 hover:text-rose-800 p-1.5">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={exclOpen} onOpenChange={setExclOpen}>
        <DialogContent className="max-w-lg" data-testid="period-exclusion-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarRange className="w-5 h-5 text-amber-700" /> {exclEdit ? "تعديل فترة الاستثناء" : "استثناء من فترة"}
            </DialogTitle>
            <DialogDescription className="sr-only">تحديد فترة وموظفين لاستثنائهم من المساءلات والإحصائيات خلالها</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-slate-500 leading-6">
            لن تُحتسب على الموظفين المحددين أي مساءلات أو إحصائيات خلال هذه الفترة، مع بقاء سجلاتهم ظاهرة في سجل الدوام.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">تاريخ بداية الاستثناء</Label>
              <DateInput type="date" value={exclFrom} onChange={(e) => setExclFrom(e.target.value)}
                className="h-9" data-testid="exclusion-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">تاريخ نهاية الاستثناء</Label>
              <DateInput type="date" value={exclTo} onChange={(e) => setExclTo(e.target.value)}
                className="h-9" data-testid="exclusion-date-to" />
            </div>
          </div>
          <div className="border rounded-xl overflow-hidden">
            <label className="flex items-center gap-2 px-3 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-600 cursor-pointer">
              <Checkbox data-testid="exclusion-check-all"
                checked={!!rows && rows.length > 0 && exclSel.size === rows.length}
                onCheckedChange={(c) => setExclSel(c ? new Set((rows || []).map((e) => e.id)) : new Set())} />
              تحديد الكل ({rows?.length || 0})
            </label>
            <div className="max-h-56 overflow-y-auto divide-y" data-testid="exclusion-employee-list">
              {(rows || []).map((e) => (
                <label key={e.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <Checkbox data-testid={`exclusion-check-${e.id}`}
                    checked={exclSel.has(e.id)}
                    onCheckedChange={(c) => setExclSel((prev) => {
                      const next = new Set(prev);
                      if (c) next.add(e.id); else next.delete(e.id);
                      return next;
                    })} />
                  <span className="truncate">{e.name}</span>
                  {e.employee_id && <span className="text-[10px] text-slate-400 font-mono" dir="ltr">{e.employee_id}</span>}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={savePeriodExclusion} disabled={exclSaving}
            data-testid="btn-save-exclusion"
            className="w-full bg-emerald-900 hover:bg-emerald-950 text-white">
            {exclSaving ? "جارٍ الحفظ..." : `حفظ الاستثناء${exclSel.size ? ` (${exclSel.size} موظف)` : ""}`}
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editEmp} onOpenChange={(v) => !v && setEditEmp(null)}>
        <DialogContent className="max-w-lg" dir="rtl" data-testid="edit-employee-modal">
          <DialogHeader><DialogTitle>تعديل بيانات الموظف</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div className="space-y-1.5">
              <Label>الاسم الكامل *</Label>
              <Input value={editForm.name} data-testid="edit-employee-name-input" required
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["employee_id", "رقم الهوية (السجل المدني)", "ltr", "edit-employee-id-input"],
                ["job_number", "رقم الوظيفة", "ltr", "edit-employee-jobnumber-input"],
                ["specialty", "التخصص", undefined, "edit-employee-specialty-input"],
                ["rank_level", "المستوى / المرتبة", undefined, "edit-employee-rank-input"],
                ["phone", "رقم الجوال", "ltr", "edit-employee-phone-input"],
              ].map(([key, label, dir, testId]) => (
                <div key={key} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Input value={editForm[key]} data-testid={testId} dir={dir}
                    onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })} />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label>العمل الحالي</Label>
                <JobSelect value={editForm.current_job || ""} testId="edit-employee-job-select"
                  onChange={(v) => setEditForm({ ...editForm, current_job: v })} />
              </div>
            </div>
            <div className="space-y-2.5 border-t pt-3" data-testid="weekly-exemption-section">
              <Label className="text-emerald-900 font-bold">استئذان أسبوعي دائم (اختياري)</Label>
              <p className="text-xs text-stone-500 leading-relaxed">
                أيام محددة يُعفى فيها الموظف تلقائياً كل أسبوع من الأنواع المختارة — بلا مساءلات ولا حسم ولا أثر في التقييم
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-2" data-testid="weekly-exemption-days">
                {[["الأحد", 6], ["الاثنين", 0], ["الثلاثاء", 1], ["الأربعاء", 2], ["الخميس", 3]].map(([lbl, d]) => (
                  <label key={d} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox data-testid={`weekly-exemption-day-${d}`}
                      checked={(editForm.weekly_exemption?.days || []).includes(d)}
                      onCheckedChange={(v) => {
                        const cur = editForm.weekly_exemption || { days: [], types: [] };
                        const days = v ? [...cur.days, d] : cur.days.filter((x) => x !== d);
                        setEditForm({ ...editForm, weekly_exemption: { ...cur, days } });
                      }} />
                    {lbl}
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-2" data-testid="weekly-exemption-types">
                {[
                  ["lateness", "التأخر", "weekly-exemption-type-lateness"],
                  ["early_leave", "الانصراف المبكر", "weekly-exemption-type-early"],
                  ["no_checkout", "بدون بصمة انصراف", "weekly-exemption-type-nocheckout"],
                ].map(([key, lbl, tid]) => (
                  <label key={key} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox data-testid={tid}
                      checked={(editForm.weekly_exemption?.types || []).includes(key)}
                      onCheckedChange={(v) => {
                        const cur = editForm.weekly_exemption || { days: [], types: [] };
                        const types = v ? [...cur.types, key] : cur.types.filter((x) => x !== key);
                        setEditForm({ ...editForm, weekly_exemption: { ...cur, types } });
                      }} />
                    {lbl}
                  </label>
                ))}
                <label className="flex items-center gap-1.5 text-sm cursor-pointer font-semibold text-emerald-900">
                  <Checkbox data-testid="weekly-exemption-type-all"
                    checked={["lateness", "early_leave", "no_checkout"].every((t) => (editForm.weekly_exemption?.types || []).includes(t))}
                    onCheckedChange={(v) => {
                      const cur = editForm.weekly_exemption || { days: [], types: [] };
                      setEditForm({ ...editForm, weekly_exemption: { ...cur, types: v ? ["lateness", "early_leave", "no_checkout"] : [] } });
                    }} />
                  جميعها
                </label>
              </div>
            </div>
            <Button type="submit" data-testid="btn-save-edit-employee" disabled={busy}
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-white">حفظ التعديلات</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" dir="rtl" data-testid="add-employee-modal">
          <DialogHeader><DialogTitle>إضافة / تحديث موظف</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-1.5">
              <Label>الاسم الكامل *</Label>
              <Input value={form.name} data-testid="employee-name-input" required
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="مثال: عبدالله القحطاني" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {field("employee_id", "رقم الهوية (السجل المدني)", "10xxxxxxxx", "ltr", "employee-id-input")}
              {field("job_number", "رقم الوظيفة", "123456", "ltr", "employee-jobnumber-input")}
              {field("specialty", "التخصص", "مثال: رياضيات", undefined, "employee-specialty-input")}
              {field("rank_level", "المستوى / المرتبة", "مثال: المستوى السادس", undefined, "employee-rank-input")}
              <div className="space-y-1.5">
                <Label>العمل الحالي</Label>
                <JobSelect value={form.current_job} testId="employee-job-select"
                  onChange={(v) => setForm({ ...form, current_job: v })} />
              </div>
              {field("phone", "رقم الجوال", "05xxxxxxxx", "ltr", "employee-phone-input")}
            </div>
            <Button type="submit" data-testid="btn-save-employee"
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-white">حفظ</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setPreview(null); setImportFile(null); setMapping({}); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="employees-import-modal">
          <DialogHeader>
            <DialogTitle>استيراد الموظفين من ملف Excel</DialogTitle>
            <DialogDescription className="sr-only">رفع ملف Excel وربط أعمدته بحقول الموظفين ثم الاستيراد</DialogDescription>
          </DialogHeader>
          {!preview ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 leading-7">
                اختر ملف Excel يحتوي بيانات الموظفين بأي ترتيب أعمدة — في الخطوة التالية ستحدد أي عمود يقابل أي حقل (الاسم، رقم الهوية، التخصص...).
              </p>
              <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-2xl py-10 cursor-pointer hover:border-emerald-600 hover:bg-emerald-50/40 transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`}
                data-testid="employees-excel-dropzone">
                <FileUp className="w-8 h-8 text-emerald-800" />
                <span className="text-sm font-semibold">{busy ? "جارٍ قراءة الملف..." : "اختر ملف Excel (.xlsx)"}</span>
                <span className="text-[11px] text-slate-400">الصف الأول يُعتبر صف العناوين</span>
                <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="employees-excel-file-input"
                  onChange={onPickFile} />
              </label>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-slate-50 border rounded-xl px-4 py-2.5">
                <p className="text-sm"><b>{importFile?.name}</b> — {preview.total_rows} صف</p>
                <label className="text-xs text-emerald-800 hover:underline cursor-pointer flex items-center gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" /> اختيار ملف آخر
                  <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="employees-excel-repick"
                    onChange={onPickFile} />
                </label>
              </div>

              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full text-xs" data-testid="employees-excel-preview-table">
                  <thead>
                    <tr className="bg-emerald-900 text-white">
                      {preview.headers.map((h, i) => (
                        <th key={i} className="px-3 py-2 font-semibold text-center whitespace-nowrap">
                          عمود {i + 1}<br /><span className="font-normal opacity-80">{h}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.samples.map((r, ri) => (
                      <tr key={ri} className="border-t">
                        {preview.headers.map((_, ci) => (
                          <td key={ci} className="px-3 py-1.5 text-center text-slate-600 whitespace-nowrap">{r[ci] || "—"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-bold">حدد العمود المقابل لكل حقل:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="employees-mapping-grid">
                  {IMPORT_FIELDS.map((f) => (
                    <div key={f.key} className="flex items-center gap-2">
                      <span className="text-xs w-36 shrink-0 font-semibold">
                        {f.label} {f.required && <span className="text-rose-600">*</span>}
                      </span>
                      <Select value={mapping[f.key] ?? "none"}
                        onValueChange={(v) => setMapping({ ...mapping, [f.key]: v === "none" ? undefined : v })}>
                        <SelectTrigger data-testid={`map-select-${f.key}`} className="h-9 text-xs flex-1">
                          <SelectValue placeholder="— لا يوجد —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— لا يوجد —</SelectItem>
                          {preview.headers.map((h, i) => (
                            <SelectItem key={i} value={String(i)}>
                              عمود {i + 1}: {h} {preview.samples[0]?.[i] ? `(${preview.samples[0][i]})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={runImport} disabled={busy || !mapping.name}
                data-testid="btn-confirm-employees-import"
                className="w-full bg-emerald-900 hover:bg-emerald-950 text-white h-11">
                {busy ? "جارٍ الاستيراد..." : `استيراد ${preview.total_rows} موظف`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
